import type { StressPattern } from '../components/lexical-legacy/nodes/StressedTextNode'

/**
 * Stress Analysis Coordinator Service
 *
 * Battle-Hardened Announcer: Epic 03 Pivot - NO MORE HEURISTICS!
 * This coordinator now ONLY returns backend-sourced or cached patterns.
 * Failures emit proper backend-error results with instrumentation.
 *
 * Architecture Note:
 * - All network calls happen here, not in nodes
 * - Backend failures surface as { pattern: null, source: 'backend-error', error: message }
 * - Instrumentation via LEXICAL_STRESS_ERROR logs and window.__LEXICAL_DEBUG__
 * - NO heuristic fallbacks - backend must provide ALL stress data
 */

export interface StressAnalysisRequest {
  word: string
  context?: string
  forceReanalysis?: boolean
}

export interface StressAnalysisResult {
  word: string
  pattern: StressPattern | null
  source: 'dictionary' | 'cache' | 'backend-error'
  error?: string
}

interface CacheEntry {
  pattern: StressPattern
  timestamp: number
  source: 'dictionary'
}

// Helper to report backend issues consistently
function reportStressBackendIssue({ word, reason }: { word: string; reason: string }): void {
  // Console log with LEXICAL_STRESS_ERROR tag for searchability
  console.error(`[LEXICAL_STRESS_ERROR] Backend stress analysis failed for "${word}": ${reason}`)

  // Instrumentation hook for debugging (guards against SSR/Jest)
  if (typeof window !== 'undefined' && window.__LEXICAL_DEBUG__) {
    window.__LEXICAL_DEBUG__.record?.({
      type: 'stress-backend-error',
      word,
      reason,
      timestamp: Date.now(),
    })
  }
}

export class StressAnalysisCoordinator {
  private cache: Map<string, CacheEntry> = new Map()
  private pendingRequests: Map<string, Promise<StressAnalysisResult>> = new Map()
  private baseUrl: string
  private cacheTTL: number = 3600000 // 1 hour default
  private fetchImpl: typeof fetch

  constructor(
    baseUrl: string = '',  // Empty string = relative URLs, proxied by nginx in production
    fetchImpl: typeof fetch = fetch
  ) {
    // Opening move: configure the coordinator with endpoint URLs and fetch implementation
    // fetchImpl allows dependency injection for testing without real network calls
    // Bind fetch to global scope to prevent "Illegal invocation" errors
    this.baseUrl = baseUrl
    this.fetchImpl = fetchImpl.bind(globalThis)
  }

  /**
   * Main play: analyze a word's stress pattern
   * This method handles caching, deduplication, and network requests
   * Epic 03: Returns backend-error on failure instead of heuristic fallbacks
   */
  async analyzeWord(request: StressAnalysisRequest): Promise<StressAnalysisResult> {
    const { word, context, forceReanalysis = false } = request
    const cleanWord = word.toLowerCase().replace(/[^a-z']/g, '')

    if (!cleanWord || cleanWord.length === 0) {
      const reason = 'Empty word after cleaning'
      reportStressBackendIssue({ word: cleanWord, reason })
      return {
        word: cleanWord,
        pattern: null,
        source: 'backend-error',
        error: reason
      }
    }

    // Check cache first (unless force reanalysis)
    if (!forceReanalysis) {
      const cached = this.getFromCache(cleanWord)
      if (cached) {
        return {
          word: cleanWord,
          pattern: cached.pattern,
          source: 'cache'
        }
      }
    }

    // Time to tackle the tricky bit: deduplicate concurrent requests for the same word
    // If another request is already in flight, wait for it instead of making duplicate calls
    const existingRequest = this.pendingRequests.get(cleanWord)
    if (existingRequest && !forceReanalysis) {
      return existingRequest
    }

    // Create new request promise
    const requestPromise = this.performAnalysis(cleanWord, context)

    // Track it to deduplicate
    this.pendingRequests.set(cleanWord, requestPromise)

    try {
      const result = await requestPromise

      // Cache successful results ONLY (no caching of errors)
      if (result.pattern && !result.error && result.source === 'dictionary') {
        this.addToCache(cleanWord, result.pattern, result.source)
      }

      return result
    } finally {
      // Victory lap: clean up the pending request tracker
      this.pendingRequests.delete(cleanWord)
    }
  }

  /**
   * Big play: perform the actual stress analysis ONLY via backend
   * Epic 03: NO MORE HEURISTICS - backend failures return error result
   * This is where all the async work happens, safely outside of Lexical nodes
   */
  private async performAnalysis(word: string, _context?: string): Promise<StressAnalysisResult> {
    // Opening move: try backend dictionary lookup
    try {
      const dictResult = await this.dictionaryLookup(word)
      if (dictResult) {
        return {
          word,
          pattern: dictResult,
          source: 'dictionary'
        }
      }

      // Backend returned no results - this is a backend failure (missing dictionary data)
      const reason = 'Backend dictionary returned no results'
      reportStressBackendIssue({ word, reason })
      return {
        word,
        pattern: null,
        source: 'backend-error',
        error: reason
      }
    } catch (error) {
      // Network or backend error
      const reason = error instanceof Error ? error.message : 'Unknown backend error'
      reportStressBackendIssue({ word, reason })
      return {
        word,
        pattern: null,
        source: 'backend-error',
        error: reason
      }
    }
  }

  /**
   * Network call: dictionary lookup for stress patterns
   * Epic 03: Uses injected fetch for testability, proper error handling
   */
  private async dictionaryLookup(word: string): Promise<StressPattern | null> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 2000) // 2 second timeout

    try {
      const response = await this.fetchImpl(
        `${this.baseUrl}/api/dictionary/stress/${encodeURIComponent(word)}`,
        { signal: controller.signal }
      )

      clearTimeout(timeoutId)

      if (!response.ok) {
        return null
      }

      const data = await response.json()

      if (!data.found || !data.syllables || !data.stress_pattern) {
        return null
      }

      // Convert API response to our StressPattern format
      // Epic 06: Preserve three-level stress (0=unstressed, 1=primary, 2=secondary)
      const syllables = data.syllables.map((syllableText: string, index: number) => ({
        text: syllableText,
        stressed: data.stress_pattern[index] > 0,
        confidence: data.confidence || 1.0,
        position: index,
        overridden: false,
        stressLevel: data.stress_pattern[index] as 0 | 1 | 2,
      }))

      return {
        syllables,
        overridden: false,
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Backend timeout for "${word}"`)
      }
      throw error
    }
  }

  /**
   * Cache management: retrieve from cache if not expired
   */
  private getFromCache(word: string): CacheEntry | null {
    const entry = this.cache.get(word)
    if (!entry) return null

    // Check if expired
    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(word)
      return null
    }

    return entry
  }

  /**
   * Cache management: add successful results to cache
   * Epic 03: Only caches dictionary results, no heuristics
   */
  private addToCache(word: string, pattern: StressPattern, source: 'dictionary'): void {
    this.cache.set(word, {
      pattern,
      timestamp: Date.now(),
      source,
    })
  }

  /**
   * Clear the entire cache (useful for testing or memory management)
   */
  clearCache(): void {
    this.cache.clear()
  }

  /**
   * Get cache statistics (for monitoring)
   */
  getCacheStats(): { size: number; entries: Array<{ word: string; source: string; age: number }> } {
    const now = Date.now()
    const entries = Array.from(this.cache.entries()).map(([word, entry]) => ({
      word,
      source: entry.source,
      age: now - entry.timestamp,
    }))

    return {
      size: this.cache.size,
      entries,
    }
  }
}

// Global coordinator instance
// This ensures we have a single instance across the application
export const stressCoordinator = new StressAnalysisCoordinator()
